import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { createTurbinesColumns, filterTurbines } from './utils';
import TurbineIcon from '@/assets/icons/turbine.svg?react';
import {
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import DataTable from '@/components/DataTable';
import Checkbox from '@/components/Form/Checkbox';
import PlusButton from '@/components/Form/PlusButton';
import SearchInput from '@/components/Form/searchInput';
import { adminToolPaths } from '@/constants/adminTool';
import { PermissionsList } from '@/constants/permissionsList';
import adminStyles from '@/containers/AdminToolContainers/styles.module.css';
import { useToast } from '@/context/ToastContext';
import { useGetTurbinesList } from '@/hooks/services/Turbines';
import { RootState } from '@/store';
import { getTurbinesPermissions } from '@/utils/adminToolPermissions';

function TurbinesContainer() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const userGroup = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.userGroup
  );

  const { canCreate: canCreateTurbine } = getTurbinesPermissions(
    auditableElements,
    userGroup
  );
  const {
    data: turbinesResponse,
    isLoading: loading,
    error: queryError,
    refetch
  } = useGetTurbinesList();

  const turbines = (turbinesResponse as any)?.data || [];
  const error = queryError ? (queryError as Error).message : null;

  const filteredTurbines = useMemo(() => {
    return filterTurbines(turbines, searchTerm, showActive, showInactive);
  }, [turbines, searchTerm, showActive, showInactive]);

  const turbinesColumns = useMemo(() => {
    return createTurbinesColumns(navigate);
  }, [navigate]);

  useEffect(() => {
    if (error) {
      showToast(error || 'An error occurred while loading turbines', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => refetch()
      });
    }
  }, [error]);

  if (loading) {
    return <ContainerLoader />;
  }

  return (
    <div className={adminStyles.adminWrapper}>
      <div className={adminStyles.adminContainer}>
        <AdminCardHeader
          left={
            <>
              <TurbineIcon className={adminStyles.adminIconHeader} />
              <h2 className={adminStyles.adminTitle}>Turbines</h2>
            </>
          }
          center={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SearchInput
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search"
              />
              <Checkbox
                checked={showActive}
                onChange={setShowActive}
                label="Active"
              />
              <Checkbox
                checked={showInactive}
                onChange={setShowInactive}
                label="Inactive"
              />
            </div>
          }
          right={
            canCreateTurbine ? (
              <PlusButton
                onClick={() => navigate(adminToolPaths.turbines.newTurbine)}
                label="Add a new turbine"
              />
            ) : null
          }
        />
        <AdminTableBody>
          <DataTable
            rows={filteredTurbines}
            columns={turbinesColumns}
            initialState={{
              sorting: {
                field: 'id',
                sort: 'asc'
              }
            }}
          />
        </AdminTableBody>
      </div>
    </div>
  );
}

export default TurbinesContainer;
